import React from 'react';
import { User, Calendar, FileText, AlertTriangle, Clock } from 'lucide-react';
import Button from '../../../components/ui/Button';

const DocumentApprovalCard = ({ document, daysWaiting, priority, onReview }) => {
  const getCategoryColor = (category) => {
    const colors = {
      contract: 'bg-emerald-100 text-emerald-700',
      personal: 'bg-gray-100 text-gray-700',
      compliance: 'bg-amber-100 text-amber-700',
      employment_letter: 'bg-purple-100 text-purple-700'
    };
    return colors?.[category] || 'bg-gray-100 text-gray-700';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      critical: 'bg-red-100 text-red-700 border-red-300',
      high: 'bg-orange-100 text-orange-700 border-orange-300',
      medium: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      low: 'bg-blue-100 text-blue-700 border-blue-300'
    };
    return colors?.[priority] || 'bg-gray-100 text-gray-700 border-gray-300';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-4">
        {/* Left Section - Document Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-4">
            {/* Icon */}
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-gray-600" />
              </div>
            </div>

            {/* Document Details */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-lg font-semibold text-gray-900 truncate">
                  {document?.documentName}
                </h3>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(document?.category)}`}>
                  {document?.category?.replace('_', ' ')?.toUpperCase()}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  <span className="truncate">
                    {document?.employeeName} ({document?.employeeNumber})
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  <span>{document?.department}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>Submitted: {formatDate(document?.submittedForApprovalAt)}</span>
                </div>
              </div>

              {document?.description && (
                <p className="mt-2 text-sm text-gray-600 line-clamp-2">
                  {document?.description}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Right Section - Priority & Actions */}
        <div className="flex flex-col items-end gap-3">
          {/* Priority Badge */}
          <div className={`px-3 py-2 rounded-lg border ${getPriorityColor(priority)} flex items-center gap-2`}>
            {priority === 'critical' || priority === 'high' ? (
              <AlertTriangle className="w-4 h-4" />
            ) : (
              <Clock className="w-4 h-4" />
            )}
            <span className="text-sm font-medium">
              {daysWaiting} {daysWaiting === 1 ? 'day' : 'days'} waiting
            </span>
          </div>

          {/* Review Button */}
          <Button
            onClick={onReview}
            iconName="Eye"
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            Review Document
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DocumentApprovalCard;